/**
 * Probe listed agent types for `configOptions` (models, modes, …).
 *
 * When `forceAllTypes` is false, skips types that already have a non-empty snapshot in local SQLite.
 * Upserts are hash-gated (see {@link upsertCliAgentCapabilityCache}); returns how many types changed.
 */

import type { LogFn } from '../../log.js';
import { yieldToEventLoop } from '../../runtime/yield-to-event-loop.js';
import { isCliImmediateShutdownRequested } from '../../runtime/cli-process-interrupt.js';
import { hasNonEmptyAgentCapabilityCache } from '../../sqlite/agent-capability-cache.js';
import { withCliSqlite } from '../../sqlite/cli-database.js';
import { probeOneAgentTypeForCapabilities, type ReportAgentCapabilitiesFn } from './probe-one-agent-type-for-capabilities.js';

export type { ReportAgentCapabilitiesFn };

export async function probeAgentCapabilitiesForDetectedTypes(params: {
  agentTypes: readonly string[];
  cwd: string;
  workspaceId: string;
  log: LogFn;
  reportAgentCapabilities?: ReportAgentCapabilitiesFn;
  bridgeReport?: boolean;
  /** When true, probe every listed type even when SQLite already has a snapshot (lazy refresh). */
  forceAllTypes?: boolean;
  /** When provided, abort probing once this returns false (e.g. superseded reconnect warmup). */
  shouldContinue?: () => boolean;
}): Promise<number> {
  const {
    agentTypes,
    cwd,
    workspaceId,
    log,
    reportAgentCapabilities,
    bridgeReport = true,
    forceAllTypes = false,
    shouldContinue,
  } = params;
  const canContinue = () =>
    !isCliImmediateShutdownRequested() && shouldContinue?.() !== false;
  let changedCount = 0;
  for (let i = 0; i < agentTypes.length; i++) {
    if (!canContinue()) return changedCount;
    if (i > 0) await yieldToEventLoop();
    const agentType = agentTypes[i]!;
    if (!agentType.trim()) continue;
    if (!forceAllTypes) {
      try {
        if (
          process.env.BUILDAUTOMATON_FORCE_PROBE_ACP_CAPABILITIES !== '1' &&
          (await withCliSqlite((db) => hasNonEmptyAgentCapabilityCache(db, workspaceId, agentType)))
        ) {
          continue;
        }
      } catch {
        /* continue with probe */
      }
    }
    const changed = await probeOneAgentTypeForCapabilities({
      agentType,
      cwd,
      workspaceId,
      log,
      reportAgentCapabilities,
      bridgeReport,
      shouldContinue: canContinue,
    });
    if (changed) changedCount += 1;
  }
  return changedCount;
}
